import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { blueButtonClass } from 'src/components/common/common';
import { isUnsetWorkDateTime } from 'src/utils/date/dateUtils';
import { calculateLotQty, usePRD3012_R01Store } from './Store_PRD3012_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { PRD3012_R01WorkTimeProcessType } from './Dto_PRD3012_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { toLocalDateTime } from 'src/utils/string/stringUtil';

interface Props {
	handleWorkTimeClick: (productOrderNo: string, processType: PRD3012_R01WorkTimeProcessType, manualTime?: string, rawTime?: { year: string, month: string, day: string, hour: string, minute: string }) => Promise<boolean>;
}

const getWorkDateTimeParts = (value: string | undefined) => {
	if (!value) return null;
	const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
	if (!match) return null;
	return {
		year: match[1],
		month: match[2],
		day: match[3],
		hour: match[4],
		minute: match[5],
	};
};

const rowLabelClass = 'w-[90px] min-w-[90px] shrink-0 text-left font-medium';

const timeInputWrapperOption = {
	wrapperStyle: 'w-[50px] min-w-[50px] mb-0',
	titleStyle: 'hidden',
	inputStyle: 'w-[50px] min-w-[50px]',
};

const displayFieldWrapperOption = (width: string) => ({
	wrapperStyle: `${width} mb-0`,
	titleStyle: 'hidden',
	inputStyle: 'w-full min-w-0',
});

export const WorkTimeModal_PRD3012_R01 = ({
	handleWorkTimeClick
}: Props) => {
	const { jobTeamList } = useCommonCodeStore();
	const {
		workTimeModalVisible, setWorkTimeModalVisible,
		workTimeModalType,
		editPRD3012_R01,
		PRD3012_R01LotList,
		deletedPRD3012_R01LotList,
	} = usePRD3012_R01Store();

	const [year, setYear] = useState<string>('');
	const [month, setMonth] = useState<string>('');
	const [day, setDay] = useState<string>('');
	const [hour, setHour] = useState<string>('');
	const [minute, setMinute] = useState<string>('');

	const isStartModal = workTimeModalType === 'start';

	const jobTeamLabel = useMemo(() => {
		const jobTeam = editPRD3012_R01?.jobTeam;
		if (!jobTeam) return '';
		return jobTeamList.find(value => value.minorCd === jobTeam)?.codeNm ?? jobTeam;
	}, [editPRD3012_R01?.jobTeam, jobTeamList]);

	const totalLotQty = useMemo(
		() => calculateLotQty(PRD3012_R01LotList, deletedPRD3012_R01LotList),
		[PRD3012_R01LotList, deletedPRD3012_R01LotList],
	);

	const productionQtyDisplay = isStartModal
		? (editPRD3012_R01?.pendingLotQty ?? 0)
		: totalLotQty;

	useEffect(() => {
		if (workTimeModalVisible) {
			const time = isStartModal ? editPRD3012_R01?.workStartDt : editPRD3012_R01?.workEndDt;
			const existingParts = !isUnsetWorkDateTime(time) ? getWorkDateTimeParts(time) : null;
			const currentParts = getWorkDateTimeParts(toLocalDateTime(new Date()));
			const dateParts = existingParts ?? currentParts;

			setYear(dateParts?.year ?? '');
			setMonth(dateParts?.month ?? '');
			setDay(dateParts?.day ?? '');

			if (existingParts) {
				setHour(existingParts.hour);
				setMinute(existingParts.minute);
			} else {
				setHour('');
				setMinute('');
			}
		}
	}, [workTimeModalVisible, isStartModal, editPRD3012_R01]);

	const handleModify = async () => {
		if (!month || !day || !hour || !minute) {
			showWarning('시간을 모두 입력해주세요.');
			return;
		}

		const pad = (value: string) => value.padStart(2, '0');
		const manualTime = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:00`;
		const rawTime = { year, month, day, hour, minute };

		let success = false;
		if (editPRD3012_R01?.productOrderNo) {
			if (isStartModal) {
				success = await handleWorkTimeClick(editPRD3012_R01.productOrderNo, 'workStart', manualTime, rawTime);
			} else {
				success = await handleWorkTimeClick(editPRD3012_R01.productOrderNo, 'workEnd', manualTime, rawTime);
			}
		}
		if (success) {
			setWorkTimeModalVisible(false);
		}
	};

	const handleReset = async () => {
		let success = false;
		if (editPRD3012_R01?.productOrderNo) {
			if (isStartModal) {
				success = await handleWorkTimeClick(editPRD3012_R01.productOrderNo, 'workStartReset');
			} else {
				success = await handleWorkTimeClick(editPRD3012_R01.productOrderNo, 'workEndReset');
			}
		}
		if (success) {
			setWorkTimeModalVisible(false);
		}
	};

	const handleMinuteKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			void handleModify();
		}
	}, [year, month, day, hour, minute, isStartModal, editPRD3012_R01, handleWorkTimeClick, setWorkTimeModalVisible]);

	if (!workTimeModalVisible) return null;

	return (
		<ModalWrapper
			size="hsm"
			width={520}
			overlay={true}
			onOverlayClick={() => setWorkTimeModalVisible(false)}
			onEscKeyDown={() => setWorkTimeModalVisible(false)}
		>
			<div className="flex flex-col h-full min-h-[320px]">
			<div className="shrink-0 flex items-center px-[24px] py-[12px] bg-gray-100 border-b border-gray-200">
				<p className="font-bold">
					{isStartModal ? '작업시작' : '작업종료'}
				</p>
			</div>

			<div className="flex-1 min-h-0 flex flex-col px-[24px] py-[16px] gap-[12px] overflow-y-auto">
				<div className="flex items-center gap-[8px] flex-wrap">
					<span className={rowLabelClass}>
						{isStartModal ? '시작일시' : '종료시간'} :
					</span>
					<InputForm
						title="월"
						name="month"
						value={month}
						onChange={(e) => setMonth(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span>월</span>
					<InputForm
						title="일"
						name="day"
						value={day}
						onChange={(e) => setDay(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span>일</span>
					<InputForm
						title="시"
						name="hour"
						value={hour}
						onChange={(e) => setHour(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
					/>
					<span>시</span>
					<InputForm
						title="분"
						name="minute"
						value={minute}
						onChange={(e) => setMinute(e.target.value)}
						editable={true}
						maxLength={2}
						wrapperOption={timeInputWrapperOption}
						titleless={true}
						dragSelectAll={true}
						onKeyDown={handleMinuteKeyDown}
					/>
					<span>분</span>
				</div>

				<div className="flex items-center gap-[8px]">
					<span className={rowLabelClass}>작업팀 :</span>
					<InputForm
						title="작업팀"
						name="jobTeamDisplay"
						value={jobTeamLabel}
						editable={false}
						wrapperOption={displayFieldWrapperOption('w-[200px] min-w-[200px]')}
						titleless={true}
					/>
				</div>

				<div className="flex items-center gap-[8px]">
					<span className={rowLabelClass}>작업자 :</span>
					<InputForm
						title="작업자코드"
						name="orderEmpDisplay"
						value={editPRD3012_R01?.orderEmp || ''}
						editable={false}
						wrapperOption={displayFieldWrapperOption('w-[120px] min-w-[120px]')}
						titleless={true}
					/>
					<InputForm
						title="작업자명"
						name="orderEmpNmDisplay"
						value={editPRD3012_R01?.orderEmpNm || ''}
						editable={false}
						wrapperOption={displayFieldWrapperOption('w-[160px] min-w-[160px]')}
						titleless={true}
					/>
				</div>

				<div className="flex items-center gap-[8px]">
					<span className={rowLabelClass}>생산량 :</span>
					<InputForm
						title="생산량"
						name="productionQtyDisplay"
						value={productionQtyDisplay}
						editable={false}
						wrapperOption={displayFieldWrapperOption('w-[200px] min-w-[200px]')}
						titleless={true}
					/>
				</div>
			</div>

			<div className="shrink-0 flex items-center justify-end px-[24px] py-[12px] gap-[8px] border-t border-gray-200">
				<button
					className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
					onClick={handleReset}
				>
					초기화
				</button>
				<button
					className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
					onClick={handleModify}
				>
					확인
				</button>
				<button
					className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
					onClick={() => setWorkTimeModalVisible(false)}
				>
					취소
				</button>
			</div>
			</div>
		</ModalWrapper>
	);
};
